"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DropdownItem = void 0;
exports.useDropdownItem = useDropdownItem;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _mergeRefs = _interopRequireDefault(require("merge-refs"));
var _useStatefulRefValue = _interopRequireDefault(require("@ux/component-utilities/use-stateful-ref-value"));
var _canUseDom = _interopRequireDefault(require("@ux/component-utilities/can-use-dom"));
var _isRightClick = _interopRequireDefault(require("@ux/component-utilities/is-right-click"));
var _composeEventHandlers = _interopRequireDefault(require("@ux/component-utilities/compose-event-handlers"));
var _dropdown = require("./dropdown");
var _util = require("../util");
var _descendants = require("../descendants");
const _excluded = ["disabled", "index", "isLink", "onClick", "onDragStart", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseMove", "onMouseUp", "onSelect", "onFocus", "valueText", "ref"],
  _excluded2 = ["as"];
/* eslint-disable no-undefined */
/* eslint-disable max-statements */
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @type ItemHook
 * @property {object} data Derived data from Dropdown state
 * @property {boolean} data.isDisabled Is item disabled
 * @property {boolean} data.isSelected Is item selected
 * @property {boolean} data.isActive Is item visually focused
 * @property {string} data.size Size variant applied
 * @property {object} props Props to be spread onto element
 * @property {string} props.id HTMLElement id used for item
 * @property {number} props.tabIndex TabIndex
 * @property {React.ForwardedRef} props.ref Composed Ref
 * @property {string} "props.aria-disabled" Aria disabled for accessibility
 * @property {string} "props.data-selected" Aria selected for accessibility
 * @property {string} "props.data-visually" Visually focused data attribute
 * @property {string} "props.data-valuetext" Value text used for the item
 * @property {function} props.onClick Composed Click handler
 * @property {function} props.onDragStart Composed DragStart handler
 * @property {function} props.onMouseDown Composed MouseDown handler
 * @property {function} props.onMouseEnter Composed MouseEnter handler
 * @property {function} props.onMouseLeave Composed MouseLeave handler
 * @property {function} props.onMouseMove Composed MouseMove handler
 * @property {function} props.onFocus Composed Focus handler
 * @property {function} props.onMouseUp Composed MouseUp handler
 */
/**
 * Handles registering the item into the context while also providing all necessary props to support accessibility and interaction
 * @param {Object} props useDropdownItem props
 * @param {boolean} [props.disabled=false] Is the item disabled
 * @param {number} props.index Explicit index of item
 * @param {boolean} [props.isLink=false] Should item be treated as a link, if true the select action will trigger a click event
 * @param {function} props.onClick Click event handler
 * @param {function} props.onDragStart DragStart event handler
 * @param {function} props.onMouseDown Mousedown event handler
 * @param {function} props.onMouseEnter MouseEnter event handler
 * @param {function} props.onMouseLeave MouseLeave event handler
 * @param {function} props.onMouseMove MouseMove event handler
 * @param {function} props.onMouseUp MouseUp event handler
 * @param {function} props.onSelect Select callback used when item is selected
 * @param {function} props.onFocus Focus callback when item is focused
 * @param {string} props.valueText Prop override for text value of item
 * @param {React.ForwardedRef} props.ref Composed ref
 * @returns {ItemHook} Data used to implement dropdown item
 */
function useDropdownItem(_ref) {
  let {
      disabled = false,
      index: indexProp,
      isLink = false,
      onClick,
      onDragStart,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onMouseMove,
      onMouseUp,
      onSelect,
      onFocus,
      valueText: valueTextProp,
      ref: forwardedRef
    } = _ref,
    props = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    closeOnSelect,
    dispatch,
    dropdownRef,
    mouseDownStartPosRef,
    onSelect: menuOnSelect,
    readyToSelect,
    selectCallbacks,
    size,
    triggerRef,
    state: {
      activeIndex,
      isExpanded,
      selectedIndex
    }
  } = (0, _dropdown.useDropdownContext)('useDropdownItem');
  const ownRef = _react.default.useRef(null);

  // After the ref is mounted to the DOM node, we check to see if we have an
  // explicit valueText prop before looking for the node's textContent for
  // typeahead functionality.
  const [valueText, setValueText] = _react.default.useState(valueTextProp || '');
  const setValueTextFromDOM = _react.default.useCallback(node => {
    if (!valueTextProp && node !== null && node !== void 0 && node.textContent) {
      setValueText(node.textContent);
    }
  }, [valueTextProp]);
  const mouseEventStarted = _react.default.useRef(false);
  const [element, handleRefSet] = (0, _useStatefulRefValue.default)(ownRef, null);
  const descendant = _react.default.useMemo(() => {
    return {
      element,
      key: valueText,
      disabled,
      isLink
    };
  }, [disabled, element, isLink, valueText]);
  const index = (0, _descendants.useDescendant)(descendant, _dropdown.DropdownDescendantContext, indexProp);
  const isActive = index === activeIndex && !disabled;
  const isSelected = index === selectedIndex && !disabled;
  const ref = (0, _mergeRefs.default)(forwardedRef, handleRefSet, setValueTextFromDOM);

  // Update the callback ref array on every render
  const combinedOnSelect = (0, _util.composeFuncs)(menuOnSelect, onSelect);
  selectCallbacks.current[index] = combinedOnSelect;
  function select(event) {
    var _triggerRef$current, _descendant$element;
    (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.focus();
    combinedOnSelect(descendant.key, {
      id: (_descendant$element = descendant.element) === null || _descendant$element === void 0 ? void 0 : _descendant$element.id,
      event
    });
    dispatch({
      type: _dropdown.SELECT_MENU_ITEM,
      payload: {
        index
      }
    });
    if (closeOnSelect) {
      (0, _canUseDom.default)() && window.requestAnimationFrame(() => dispatch({
        type: _dropdown.CLOSE_MENU,
        payload: {
          triggerRef
        }
      }));
    }
  }
  function handleClick(event) {
    if ((0, _isRightClick.default)(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      if (disabled) {
        event.preventDefault();
      } else {
        select(event);
      }
    }
  }
  function handleDragStart(event) {
    // Because we don't preventDefault on mousedown for links (we need the
    // native click event), clicking and holding on a link triggers a
    // dragstart which we don't want.
    if (isLink) {
      event.preventDefault();
    }
  }
  function handleMouseDown(event) {
    if ((0, _isRightClick.default)(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      // Signal that the mouse is down so we can call the right function if the
      // user is clicking on a link.
      mouseEventStarted.current = true;
    } else {
      event.preventDefault();
    }
  }
  function handleMouseEnter() {
    const doc = (0, _util.getOwnerDocument)(dropdownRef.current);
    if (!doc) return;
    if (!isActive && index != null && !disabled) {
      if (dropdownRef !== null && dropdownRef !== void 0 && dropdownRef.current && dropdownRef.current !== doc.activeElement && ownRef.current !== doc.activeElement) {
        dropdownRef.current.focus();
      }
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index
        }
      });
    }
  }
  function handleMouseLeave() {
    // Clear out selection when mouse over a non-dropdown-item child.
    dispatch({
      type: _dropdown.CLEAR_ACTIVE_INDEX
    });
  }
  function handleMouseMove(event) {
    if (!readyToSelect.current) {
      const threshold = 8;
      const deltaX = Math.abs(event.clientX - mouseDownStartPosRef.current.x);
      const deltaY = Math.abs(event.clientY - mouseDownStartPosRef.current.y);
      if (deltaX > threshold || deltaY > threshold) {
        readyToSelect.current = true;
      }
    }
    if (!isActive && index != null && !disabled) {
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index,
          dropdownRef
        }
      });
    }
  }
  function handleFocus() {
    readyToSelect.current = true;
    if (!isActive && index != null && !disabled) {
      dispatch({
        type: _dropdown.ACTIVE_ITEM_AT_INDEX,
        payload: {
          index
        }
      });
    }
  }
  function handleMouseUp(event) {
    if ((0, _isRightClick.default)(event.nativeEvent)) {
      return;
    }
    if (!readyToSelect.current) {
      readyToSelect.current = true;
      return;
    }
    if (isLink) {
      // If a mousedown event was initiated on a link item followed by a mouseup
      // event on the same link, we do nothing; a click event will come next and
      // handle selection. Otherwise, we trigger a click event.
      if (mouseEventStarted.current) {
        mouseEventStarted.current = false;
      } else if (ownRef.current) {
        ownRef.current.click();
      }
    } else if (!disabled) {
      select(event);
    }
  }
  _react.default.useEffect(() => {
    if (isExpanded) {
      // When the dropdown opens, wait for about half a second before enabling
      // selection. This is designed to mirror dropdown menus on macOS, where
      // opening a menu on top of a trigger would otherwise result in an
      // immediate accidental selection once the click trigger is released.
      const id = window.setTimeout(() => {
        readyToSelect.current = true;
      }, 400);
      return () => {
        window.clearTimeout(id);
      };
    }
    // When the dropdown closes, reset readyToSelect for the next interaction.
    readyToSelect.current = false;
  }, [isExpanded, readyToSelect]);
  return {
    data: {
      isDisabled: disabled,
      isSelected,
      isActive,
      size
    },
    props: _objectSpread(_objectSpread({
      'id': (0, _dropdown.useItemId)(index),
      'tabIndex': isActive ? 0 : -1
    }, props), {}, {
      ref,
      'aria-disabled': disabled ? '' : undefined,
      'data-selected': isSelected ? '' : undefined,
      'data-visually-focused': isActive ? '' : undefined,
      'data-valuetext': valueText,
      'onClick': (0, _composeEventHandlers.default)(onClick, handleClick),
      'onDragStart': (0, _composeEventHandlers.default)(onDragStart, handleDragStart),
      'onMouseDown': (0, _composeEventHandlers.default)(onMouseDown, handleMouseDown),
      'onMouseEnter': (0, _composeEventHandlers.default)(onMouseEnter, handleMouseEnter),
      'onMouseLeave': (0, _composeEventHandlers.default)(onMouseLeave, handleMouseLeave),
      'onMouseMove': (0, _composeEventHandlers.default)(onMouseMove, handleMouseMove),
      'onFocus': (0, _composeEventHandlers.default)(onFocus, handleFocus),
      'onMouseUp': (0, _composeEventHandlers.default)(onMouseUp, handleMouseUp)
    })
  };
}

/**
 * DropdownItem
 */
const DropdownItem = exports.DropdownItem = /*#__PURE__*/_react.default.forwardRef((_ref2, forwardedRef) => {
  let {
      as: Comp = 'div'
    } = _ref2,
    rest = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
  const {
    props
  } = useDropdownItem(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /*#__PURE__*/_react.default.createElement(Comp, props);
});
DropdownItem.propTypes = {
  as: _propTypes.default.node
};
DropdownItem.displayName = 'DropdownItem';